/**
 * A cheap stand-in ("surrogate") for tornprobability.com's success calculation (owner, 2026-10-22: "automate the role
 * requirements and find the best most optimal algorithm for it"; docs/oc-suitability.md section 9).
 *
 * tornprobability's CalculateSuccess is a deterministic function of a crime's pass rates (one per seat, 0–100) that it
 * doesn't publish. The Omni-Torn server asks it at a few dozen well-spread points per crime (a Latin hypercube, below),
 * and fits a small polynomial to the answers — so the requirement search (oc-requirements.ts) can try thousands of
 * lineups without asking tornprobability thousands of times, and the extension can work out "what if" lineups locally.
 * A surrogate number is always labelled "≈" — only CalculateSuccess' own answer is tornprobability's figure.
 *
 * Model: success ≈ link⁻¹(Σ coef × feature(u)), u = (rate − 50) / 50 per seat (−1…1) — or u = ln(rate / 100), where a
 * product of checkpoint chances is linear. Feature sets: linear; + squares; + every pair's product (full quadratic);
 * + cubes (and linear / quadratic in the logarithms). Link: identity, logit or log. The candidate with the
 * lowest leave-one-out error on the samples is kept (least squares with a tiny ridge; the leave-one-out residual of a
 * linear fit is exact: e / (1 − h)). Pure.
 */

export type SurrogateKind = 'lin' | 'sq' | 'quad' | 'cubic' | 'llin' | 'lquad';
export type SurrogateLink = 'id' | 'logit' | 'log';

export interface Surrogate {
  /** Number of seats (tornprobability's parameter count; seats in its GetRoleNames order). */
  n: number;
  kind: SurrogateKind;
  link: SurrogateLink;
  coef: number[];
  /** Errors in chance-of-success units (0…1): leave-one-out RMSE; held-out max / mean (null: no held-out points). */
  err: { loo: number; heldMax: number | null; heldMean: number | null; samples: number; held: number };
}

const KINDS: SurrogateKind[] = ['lin', 'llin', 'sq', 'quad', 'lquad', 'cubic'];
const LINKS: SurrogateLink[] = ['id', 'logit', 'log'];

/** How many coefficients a feature set has for n seats. */
export function termCount(kind: SurrogateKind, n: number): number {
  const lin = n + 1;
  if (kind === 'lin' || kind === 'llin') return lin;
  if (kind === 'lquad') return lin + n + (n * (n - 1)) / 2;
  if (kind === 'sq') return lin + n;
  if (kind === 'quad') return lin + n + (n * (n - 1)) / 2;
  return lin + 2 * n + (n * (n - 1)) / 2;
}

/** The features of one point (rates 0–100). */
export function features(kind: SurrogateKind, x: readonly number[]): number[] {
  // 'llin' / 'lquad' work on the logarithm of each pass rate: a product of checkpoint chances is linear there.
  const logs = kind === 'llin' || kind === 'lquad';
  const u = x.map((r) => (logs ? Math.log(Math.max(1, r) / 100) : (r - 50) / 50));
  const out = [1, ...u];
  if (kind === 'lin' || kind === 'llin') return out;
  for (const a of u) out.push(a * a);
  if (kind === 'sq') return out;
  for (let i = 0; i < u.length; i++) for (let j = i + 1; j < u.length; j++) out.push(u[i]! * u[j]!);
  if (kind === 'quad' || kind === 'lquad') return out;
  for (const a of u) out.push(a * a * a);
  return out;
}

const EPS = 0.002;
const logit = (p: number) => {
  const q = Math.min(1 - EPS, Math.max(EPS, p));
  return Math.log(q / (1 - q));
};
const sigmoid = (z: number) => 1 / (1 + Math.exp(-z));
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** The surrogate's chance of success for these pass rates (0…1). */
export function predict(s: Surrogate, x: readonly number[]): number {
  const f = features(s.kind, x);
  let z = 0;
  for (let i = 0; i < f.length; i++) z += (s.coef[i] ?? 0) * f[i]!;
  return unlink(s.link, z);
}

const unlink = (link: SurrogateLink, z: number) => (link === 'logit' ? sigmoid(z) : link === 'log' ? clamp01(Math.exp(Math.min(0, z))) : clamp01(z));

/** How much one more point of pass rate in each seat adds (chance per point, central difference ±h, clipped to 0–100). */
export function gradient(f: (x: number[]) => number, x: readonly number[], h = 3): number[] {
  return x.map((_, i) => {
    const up = [...x];
    const dn = [...x];
    up[i] = Math.min(100, x[i]! + h);
    dn[i] = Math.max(0, x[i]! - h);
    const d = up[i]! - dn[i]!;
    return d > 0 ? (f(up) - f(dn)) / d : 0;
  });
}

/** Solves (A) x = b in place for a small square system (Gaussian elimination, partial pivoting); null when singular. */
export function solveLinear(A: number[][], b: number[]): number[] | null {
  const n = b.length;
  const M = A.map((r, i) => [...r, b[i]!]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r]![c]!) > Math.abs(M[p]![c]!)) p = r;
    if (Math.abs(M[p]![c]!) < 1e-12) return null;
    [M[c], M[p]] = [M[p]!, M[c]!];
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const k = M[r]![c]! / M[c]![c]!;
      if (k) for (let j = c; j <= n; j++) M[r]![j]! -= k * M[c]![j]!;
    }
  }
  return M.map((r, i) => r[n]! / r[i]!);
}

/** The inverse of a small square matrix (null when singular). */
function invert(A: number[][]): number[][] | null {
  const n = A.length;
  const cols: number[][] = [];
  for (let j = 0; j < n; j++) {
    const e = new Array<number>(n).fill(0);
    e[j] = 1;
    const c = solveLinear(A, e);
    if (!c) return null;
    cols.push(c);
  }
  return A.map((_, i) => cols.map((c) => c[i]!));
}

/** One candidate fit: coefficients and its leave-one-out RMSE (in chance units), or null when it can't be fitted. */
function fitOne(kind: SurrogateKind, link: SurrogateLink, X: readonly number[][], y: readonly number[]): { coef: number[]; loo: number } | null {
  const rows = X.map((x) => features(kind, x));
  const p = rows[0]?.length ?? 0;
  if (!p || rows.length < p + 3) return null;
  const t = y.map((v) => (link === 'logit' ? logit(v) : link === 'log' ? Math.log(Math.max(EPS, v)) : v));
  const XtX = Array.from({ length: p }, () => new Array<number>(p).fill(0));
  const Xty = new Array<number>(p).fill(0);
  for (let r = 0; r < rows.length; r++) {
    const f = rows[r]!;
    for (let i = 0; i < p; i++) {
      Xty[i]! += f[i]! * t[r]!;
      for (let j = 0; j < p; j++) XtX[i]![j]! += f[i]! * f[j]!;
    }
  }
  // A tiny ridge (not on the constant): keeps nearly-collinear designs solvable without bending the fit.
  for (let i = 1; i < p; i++) XtX[i]![i]! += 1e-6 * rows.length;
  const inv = invert(XtX);
  if (!inv) return null;
  const coef = inv.map((row) => row.reduce((a, v, j) => a + v * Xty[j]!, 0));
  let sq = 0;
  for (let r = 0; r < rows.length; r++) {
    const f = rows[r]!;
    let z = 0;
    let h = 0;
    for (let i = 0; i < p; i++) {
      z += coef[i]! * f[i]!;
      let a = 0;
      for (let j = 0; j < p; j++) a += inv[i]![j]! * f[j]!;
      h += f[i]! * a;
    }
    if (h >= 0.999) return null;
    const zLoo = t[r]! - (t[r]! - z) / (1 - h);
    const pred = unlink(link, zLoo);
    sq += (pred - y[r]!) ** 2;
  }
  return { coef, loo: Math.sqrt(sq / rows.length) };
}

/**
 * The best surrogate for these samples (pass-rate vectors and tornprobability's answers, 0…1): every feature set and
 * link that the sample count allows, the lowest leave-one-out error wins (ties: the simpler). `held` = points kept out
 * of the fit, to report how far the surrogate is from the real answers on lineups it never saw. Null: too few samples.
 */
export function fitSurrogate(X: readonly number[][], y: readonly number[], held: { X: readonly number[][]; y: readonly number[] } = { X: [], y: [] }): Surrogate | null {
  const n = X[0]?.length ?? 0;
  if (!n || X.length !== y.length) return null;
  let best: { kind: SurrogateKind; link: SurrogateLink; coef: number[]; loo: number } | null = null;
  for (const kind of KINDS)
    for (const link of LINKS) {
      const f = fitOne(kind, link, X, y);
      // A more complex model must be clearly better (5%) to be chosen.
      if (f && (!best || f.loo < best.loo * 0.95)) best = { kind, link, ...f };
    }
  if (!best) return null;
  const s: Surrogate = { n, kind: best.kind, link: best.link, coef: best.coef.map((c) => Math.round(c * 1e7) / 1e7), err: { loo: best.loo, heldMax: null, heldMean: null, samples: X.length, held: held.X.length } };
  if (held.X.length) {
    const e = held.X.map((x, i) => Math.abs(predict(s, x) - held.y[i]!));
    s.err.heldMax = Math.max(...e);
    s.err.heldMean = e.reduce((a, b) => a + b, 0) / e.length;
  }
  return s;
}

/** A small seeded random number generator (mulberry32): the same scenario always gets the same sample points. */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A number from a text (FNV-1a), for seeds. */
export function hashText(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * A Latin hypercube: m points in n dimensions, whole numbers in [lo, hi], each dimension's range cut into m strips and
 * every strip used once — well spread with few points (better than random for fitting). Of `tries` candidate designs the
 * one whose closest two points are farthest apart is kept (maximin).
 */
export function latinHypercube(m: number, n: number, lo: number, hi: number, rand: () => number, tries = 20): number[][] {
  let best: number[][] = [];
  let bestGap = -1;
  for (let t = 0; t < Math.max(1, tries); t++) {
    const cols = Array.from({ length: n }, () => {
      const perm = Array.from({ length: m }, (_, i) => i);
      for (let i = m - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [perm[i], perm[j]] = [perm[j]!, perm[i]!];
      }
      return perm.map((k) => Math.round(lo + ((k + rand()) / m) * (hi - lo)));
    });
    const pts = Array.from({ length: m }, (_, i) => cols.map((c) => c[i]!));
    let gap = Infinity;
    for (let i = 0; i < m; i++) for (let j = i + 1; j < m; j++) gap = Math.min(gap, pts[i]!.reduce((a, v, k) => a + (v - pts[j]![k]!) ** 2, 0));
    if (gap > bestGap) {
      bestGap = gap;
      best = pts;
    }
  }
  return best;
}

/** Only a well-formed surrogate (from the server's answer). */
export function readSurrogate(x: unknown): Surrogate | null {
  const o = x && typeof x === 'object' ? (x as Record<string, unknown>) : null;
  if (!o) return null;
  const n = o.n;
  const kind = o.kind;
  const link = o.link;
  if (typeof n !== 'number' || !Number.isInteger(n) || n < 1 || n > 12) return null;
  if (!KINDS.includes(kind as SurrogateKind) || !LINKS.includes(link as SurrogateLink)) return null;
  const coef = Array.isArray(o.coef) ? o.coef : [];
  if (coef.length !== termCount(kind as SurrogateKind, n) || !coef.every((c) => typeof c === 'number' && Number.isFinite(c))) return null;
  const e = (o.err && typeof o.err === 'object' ? o.err : {}) as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return { n, kind: kind as SurrogateKind, link: link as SurrogateLink, coef: coef as number[], err: { loo: num(e.loo) ?? 1, heldMax: num(e.heldMax), heldMean: num(e.heldMean), samples: num(e.samples) ?? 0, held: num(e.held) ?? 0 } };
}
